
import {Raycaster, PlaneGeometry, MeshBasicMaterial, Vector3, Mesh, Scene as Scene3D, Camera} from "three";
import {ContentView} from "../view/ContentView";
import {Scene} from "../scene/Scene";
import {Building} from "../building/Building";
import {BuildingGeometry} from "../building/BuildingGeometry";
import {Main} from "../Main";
import {ISceneAddon} from "../scene/addon/ISceneAddon";
import {SceneNodeState} from "../scene/ISceneNode";

/**
 * Used to place addons (doors, windows, etc), but not accessories.
 */
export class NodePlacer
{
	private _content: ContentView;
	private _scene: Scene;
	//private _scene3D:Scene;
	//private _camera:PerspectiveCamera;
	private _element: HTMLElement;

	private _raycaster: Raycaster;

	public hitScene: Scene3D;

	private _planeGeometry: PlaneGeometry;
	private _basicMaterial: MeshBasicMaterial;

	private _savedPos: any;

	private _walls: {
		left: Mesh;
		right: Mesh;
		front: Mesh;
		back: Mesh;
	};
	private _normals: {
		left: Vector3;
		right: Vector3;
		front: Vector3;
		back: Vector3;
	};

	private _roof: {
		left: Mesh;
		right: Mesh;
	};

	constructor(contentView: ContentView)
	{
		this._content = contentView;
		this._scene = contentView.scene;
		//this._camera  = contentView.camera;
		this._element = contentView.canvas;
		//this._scene3D = contentView.scene3D;

		this._raycaster = new Raycaster();

		this.createHitScene();
		this.updateHitScene();
		this._content.config.building.propertyChange.add(this.onPropertChange);

		this._scene.changeSignal.add(this.onSceneChange);
	}

	private createHitScene()
	{
		this.hitScene = new Scene3D();

		this._planeGeometry = new PlaneGeometry(1, 1, 1, 1);
		this._basicMaterial = new MeshBasicMaterial({color: 0xFFFF00});

		this.createWalls();
		this.createRoof();
	}

	private createWalls()
	{
		var geom = this._planeGeometry;
		var mat = this._basicMaterial;

		this._walls = {
			left: new Mesh(geom, mat),
			right: new Mesh(geom, mat),
			front: new Mesh(geom, mat),
			back: new Mesh(geom, mat)
		};

		this._walls.left.name = Building.WALLS.LEFT;
		this._walls.right.name = Building.WALLS.RIGHT;
		this._walls.front.name = Building.WALLS.FRONT;
		this._walls.back.name = Building.WALLS.BACK;

		this._normals = {
			left: new Vector3(-1, 0, 0),
			right: new Vector3(1, 0, 0),
			front: new Vector3(0, 0, 1),
			back: new Vector3(0, 0, -1)
		};

		this._walls.left.rotation.y = - Math.PI / 2;
		this._walls.right.rotation.y = Math.PI / 2;
		this._walls.back.rotation.y = Math.PI;

		this.hitScene.add(this._walls.left);
		this.hitScene.add(this._walls.right);
		this.hitScene.add(this._walls.front);
		this.hitScene.add(this._walls.back);
	}

	private createRoof()
	{
		var mat = this._basicMaterial;

		var roof_side_geometry = BuildingGeometry.createRoofGeometry();
		var roof_left = new Mesh(roof_side_geometry, mat);
		var roof_right = new Mesh(roof_side_geometry, mat);

		this._roof = {
			left: roof_left,
			right: roof_right
		};

		this._roof.left.name = Building.ROOF_LEFT;
		this._roof.right.name = Building.ROOF_RIGHT;


		// name

		this.hitScene.add(this._roof.left);
		this.hitScene.add(this._roof.right);
	}

	private onPropertChange = () =>
	{
		this.updateHitScene();
		this._scene.updateAddonPositions();
	};

	private updateHitScene()
	{
		var metrics = this._scene.building.config.metrics;

		var leftWall = this._walls.left;
		var rightWall = this._walls.right;
		var frontWall = this._walls.front;
		var backWall = this._walls.back;

		var length = metrics.length;
		var width_half = metrics.width_half;


		//var height = metrics.height;

		// extend hit area
		// ideally: only extend if an item to be placed is wall item
		var wall_height = metrics.height; //* 1.5;
		var pos_y = metrics.height_half;

		leftWall.position.x = -width_half;
		leftWall.position.y = pos_y;
		leftWall.scale.x = length;
		leftWall.scale.y = wall_height;

		var singleSlopeHeight = 0;
		var configBuilding = Main.getInstance().config.building;
		if (configBuilding.single_slope.value)
		{
			singleSlopeHeight = configBuilding.width.value * configBuilding.pitch.value / 12;
		}
		rightWall.position.x = width_half;
		rightWall.position.y = pos_y + singleSlopeHeight / 2;
		rightWall.scale.x = length;
		rightWall.scale.y = wall_height + singleSlopeHeight;

		if (!configBuilding.single_slope.value)
		{
			singleSlopeHeight = configBuilding.width.value * configBuilding.pitch.value / 6; // the front and endwall needs to be higher always, not just when single slope is set.
		}

		frontWall.position.z = metrics.length_half;
		frontWall.position.y = pos_y + singleSlopeHeight / 2;
		frontWall.scale.x = metrics.width;
		frontWall.scale.y = wall_height + singleSlopeHeight;

		backWall.position.z = -metrics.length_half;
		backWall.position.y = pos_y + singleSlopeHeight / 2;
		backWall.scale.x = metrics.width;
		backWall.scale.y = wall_height + singleSlopeHeight;

		// roof

		var building = this._content.config.building;

		//var tan = building.pitch.value / 12;
		//var alpha = Math.atan(tan);
		//var roof_height = tan * width_half;
		//var top = metrics.height + roof_height;

		const overhang_front = building.overhang_end.value;
		const overhang_back = building.overhang_end.value;
		const overhang_left = building.overhang_sides.value;
		const overhang_right = building.overhang_sides.value;
		// var overhang_front = building.overhang_front.value;
		// var overhang_back  = building.overhang_back.value;
		// var overhang_left  = building.overhang_left.value;
		// var overhang_right = building.overhang_right.value;

		var roof_side_scale_x = length + overhang_front + overhang_back;
		var roof_side_scale_y_left = (width_half + overhang_left) / Math.cos(metrics.alpha);
		var roof_side_scale_y_right = (width_half + overhang_right) / Math.cos(metrics.alpha);

		var roofPosY = metrics.top + 0.1; // add some offset so walls don't overlap roof

		var roof_left = this._roof.left;
		var roof_right = this._roof.right;

		roof_left.scale.z = roof_side_scale_x;
		roof_left.scale.y = roof_side_scale_y_left;
		roof_left.position.y = roofPosY;
		roof_left.rotation.z = Math.PI / 2 + metrics.alpha;

		roof_right.scale.z = roof_side_scale_x;
		roof_right.scale.y = roof_side_scale_y_right;
		roof_right.position.y = roofPosY;
		roof_right.rotation.z = Math.PI / 2 + metrics.alpha;
		roof_right.rotation.y = Math.PI;

		// end of roof

		// if we don't render this scene updateMatrixWorld is not called
		// so it needs to be called manually

		this.hitScene.updateMatrixWorld(false);
	}

	private onSceneChange = (state: SceneNodeState, node: ISceneAddon) =>
	{
		const authView = Main.getInstance().authView;

		if (state === SceneNodeState.PLACING)
		{
			authView.disableAll();

			//while placing, rotation is disabled
			Main.getInstance().content.controller.orbitControls.enableRotate = false;

			// node becomes placing, ie. the user can move the mouse and find a place for it
			// -> add move, click listeners

			if (node.added)
			{
				// node is already added -> this is a move

				// save position (so it can be restored if the placing is cancelled)
				this._savedPos = node.getPosition();

				node.removeFromScene(this._content.building);
				this._scene.building.removeNode(node);
			}

			node.addToScene(this._content.scene3DOverlay);
		}
		else if (state === SceneNodeState.CANCELED || state === SceneNodeState.PLACED)
		{
			authView.updateButtons();
			// node is canceled or placed

			//while placing, rotation is disabled
			Main.getInstance().content.controller.orbitControls.enableRotate = true;
		}

		if (state === SceneNodeState.PLACED)
		{
			// node is placed -> add to the real scene3D

			node.removeFromScene(this._content.scene3DOverlay);

			// seems to be a three.js bug when using multiple scenes
			//node['_mesh'].__webglInit = undefined;
			//node['_mesh'].__webglActive = undefined;
			//this._content.renderer.render(this._content.scene3DOverlay, this._content.camera);


			//this._content.renderer.initWebGLObjects(this._content.scene3DOverlay);

			node.addToScene(this._content.building, true);

			this._scene.building.addNode(node);
		}

		if (state === SceneNodeState.CANCELED)
		{
			// node is canceled -> remove from overlay scene

			node.removeFromScene(this._content.scene3DOverlay);



			if (node.added)
			{
				// reset position
				node.setPosition(this._savedPos);
				this._savedPos = null;

				// seems to be a three.js bug when using multiple scenes

				//this._content.renderer.initWebGLObjects(this._content.scene3DOverlay);
				node.addToScene(this._content.building);
				this._scene.building.addNode(node);
			}
		}

		if (state === SceneNodeState.DESTROYED)
		{
			node.removeFromScene(this._content.building);
			this._scene.building.removeNode(node);
		}

		Main.getInstance().render = true;
	};

	/**
	 * Move node to where the cursor is.
	 * @param position screen space coordinates
	 */
	public updatePosition(position: number[], camera: Camera): boolean
	{
		var x = position[0];
		var y = position[1];

		var node = this._scene.placingAddon;
		var vector = new Vector3(x, y, 0);
		vector.unproject(camera);
		//this._projector.unprojectVector(vector, camera);
		this._raycaster.set(camera.position, vector.sub(camera.position).normalize());
		var hit = false;
		if (node)
		{
			hit = node.checkPlacing(this._raycaster, this._scene, this);
		}

		if (node == null || !node.hasValidPlace)
		{
			return false;
		}

		window.document.body.style.cursor = hit === true ? "move" : "";

		return hit;
	}

	public async placeNode(dispatch = true)
	{
		if (this.getPlacingNodeHasValidPlace())
		{
			return (await this._scene.addPlacingAddon(dispatch));
		}

		return false;
	}


	public get placingNode(): ISceneAddon
	{
		return this._scene.placingAddon;
	}

	public getPlacingNodeHasValidPlace()
	{
		var placingNode = this.placingNode;

		if (placingNode)
		{
			if (placingNode.id.indexOf('rollup') !== -1) /** we check the rollups elsewhere */
			{
				return true;
			}
		}

		return placingNode && placingNode.hasValidPlace;
	}
}